import { jsx } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import { ValueWithTrend, ChartTemplate, TableTemplate, TextBlockTemplate, BannerTemplate } from "@effektify/components/templates";
import { useState, useEffect } from "react";
import useLogger from "../../hooks/useLogger.js";
import { isDataKey } from "@effektify/types";
import "sprintf-js";
import "@effektify/types/guards";
import "@effektify/cache";
import useDatasetToSeries from "../../hooks/useDatasetToSeries.js";
import { isTemplateCard, isValueWithTrendTemplate, isChartTemplate, isSemiCircleCard, isLineChartCard, isTableTemplate, isTextBlockTemplate, isBannerTemplate } from "./guards.js";
import { useDataProvider } from "@effektify/data-provider";
import useCardGoals from "../../hooks/cardProps/useCardGoals.js";
import { mapValueWithTrendProps, mapGaugeProps, mapLineChartProps } from "../../utils/cardProps.js";
import useReportTranslations from "../../hooks/useReportTranslations.js";
import "../../contexts/HostContext.js";
import { useHost } from "../../contexts/useHost.js";
import { getChartData, getValueData, mapTrendsData, createTitleTranslator } from "../../utils/cardData.js";
const getTableData = (dataSources, parsedData) => {
  const sources = Object.keys(dataSources);
  const defaultDataset = {
    columns: [],
    data: []
  };
  if (sources.length === 1 && sources[0]) {
    return {
      data: dataSources[sources[0]],
      summary: null
    };
  }
  const {
    key: tableKey
  } = parsedData;
  return {
    data: dataSources[tableKey] || defaultDataset,
    summary: dataSources[`${tableKey}.summary`] || null
  };
};
const translateTableConfigTitles = (slots, t) => {
  const mapColumnTitle = col => {
    if (typeof col.title === "string") {
      return {
        ...col,
        title: t(col.title)
      };
    }
    return {
      ...col,
      title: t(col.title)
    };
  };
  if (slots?.table?.columns) {
    return [slots.table.columns.map(col => mapColumnTitle(col))];
  }
  if (slots?.tabs) {
    return slots.tabs.map(tab => tab.slots.columns.map(col => mapColumnTitle(col)));
  }
  return [];
};
const CardTemplate = t0 => {
  const $ = c(45);
  const {
    config,
    data: initialData,
    isLoading: t1,
    colorScheme: t2,
    maxHeight
  } = t0;
  const isLoading = t1 === void 0 ? false : t1;
  const colorScheme = t2 === void 0 ? "default" : t2;
  let t3;
  if ($[0] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [cardData, setCardData] = useState(t3);
  const {
    translate
  } = useReportTranslations();
  const {
    noComparison
  } = useDataProvider();
  const translateTitleSlot = createTitleTranslator(translate);
  const logger = useLogger("CardTemplate");
  const {
    onNavigate,
    components,
    helpContent
  } = useHost();
  const {
    resolveSeriesData
  } = useDatasetToSeries();
  const goalsData = useCardGoals(config.goals);
  let t4;
  let t5;
  if ($[1] !== initialData) {
    t4 = () => {
      if (initialData && Object.keys(initialData).length > 0) {
        setCardData(prev => {
          if (!prev || JSON.stringify(prev) !== JSON.stringify(initialData)) {
            return initialData;
          }
          return prev;
        });
      }
    };
    t5 = [initialData];
    $[1] = initialData;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  if (!config || isTemplateCard(config) === false) {
    logger.debug("Unknown card type", config);
    return null;
  }
  logger.debug("[CardTemplate] Rendering template card with data", cardData);
  if (isValueWithTrendTemplate(config)) {
    let t6;
    if ($[4] !== cardData || $[5] !== config || $[6] !== noComparison || $[7] !== translate) {
      t6 = mapValueWithTrendProps(config, cardData, {
        translate,
        noComparison
      });
      $[4] = cardData;
      $[5] = config;
      $[6] = noComparison;
      $[7] = translate;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    const valueWithTrendProps = t6;
    let t7;
    if ($[9] !== helpContent || $[10] !== isLoading || $[11] !== valueWithTrendProps) {
      t7 = /* @__PURE__ */jsx(ValueWithTrend, {
        ...valueWithTrendProps,
        isLoading,
        helpContent
      });
      $[9] = helpContent;
      $[10] = isLoading;
      $[11] = valueWithTrendProps;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  if (isChartTemplate(config) && config.slots.chart) {
    logger.debug("Rendering ChartTemplate", config.slots);
    const chartData = getChartData(cardData, config.slots.chart);
    if (config.slots.chart.type !== "none" && (chartData === null || Object.keys(chartData).length === 0)) {
      logger.debug("No data found for ChartTemplate", config);
      return null;
    }
    const style = config.slots.style;
    const chartProps = {
      colorScheme,
      title: translateTitleSlot(config.slots.title)
    };
    if (isSemiCircleCard(config)) {
      const gaugeProps = mapGaugeProps(config, cardData, {
        translate,
        goalsData
      });
      chartProps.footer = gaugeProps.footer;
      chartProps.chart = gaugeProps.chart;
      const GoalEditButton = components.GoalEditButton;
      if (GoalEditButton && gaugeProps.goalType) {
        chartProps.goalEditSlot = /* @__PURE__ */jsx(GoalEditButton, {
          goalType: gaugeProps.goalType,
          goal: gaugeProps.chart.goal?.goal,
          description: gaugeProps.chart.goal?.description,
          valueType: gaugeProps.chart.data?.type ?? "integer",
          currency: gaugeProps.chart.data?.currency
        });
      }
    }
    if (config.slots.chart.type === "bar_chart") {
      logger.debug("Bar_Chart", config.slots, cardData);
      const value = isDataKey(config.slots?.value || "") ? getValueData(cardData, config.slots.value) : void 0;
      const trendsData = config.slots.trends && !noComparison ? mapTrendsData(cardData, config.slots.trends, translate) : void 0;
      chartProps.chart = {
        type: "bar_chart",
        data: chartData,
        value,
        configuration: config.slots.chart.configuration,
        series: config.slots.chart.series,
        showLabels: config.slots.chart["show-labels"],
        trends: trendsData
      };
      logger.debug("BarChart prop is", chartProps);
    }
    if (config.slots.chart.type === "stacked_bar_chart") {
      logger.debug("Bar_Chart", config.slots, cardData);
      const value_0 = isDataKey(config.slots?.value || "") ? getValueData(cardData, config.slots.value) : void 0;
      const trendsData_0 = config.slots.trends && !noComparison ? mapTrendsData(cardData, config.slots.trends, translate) : void 0;
      chartProps.chart = {
        type: "stacked_bar_chart",
        data: chartData,
        value: value_0,
        configuration: config.slots.chart.configuration,
        series: config.slots.chart.series,
        showLabels: config.slots.chart["show-labels"],
        trends: trendsData_0
      };
      logger.debug("BarChart prop is", chartProps);
    }
    if (config.slots.chart.type === "mixed_chart") {
      const value_1 = isDataKey(config.slots?.value || "") ? getValueData(cardData, config.slots.value) : void 0;
      const trendsData_1 = config.slots.trends && !noComparison ? mapTrendsData(cardData, config.slots.trends, translate) : void 0;
      chartProps.chart = {
        type: "mixed_chart",
        data: chartData,
        value: value_1,
        configuration: config.slots.chart.configuration,
        series: config.slots.chart.series,
        showLabels: config.slots.chart["show-labels"],
        trends: trendsData_1
      };
    }
    if (config.slots.chart.type === "pie_chart") {
      chartProps.chart = {
        type: "pie_chart",
        data: chartData,
        configuration: config.slots.chart.configuration,
        series: config.slots.chart.series,
        showLabels: config.slots.chart["show-labels"]
      };
    }
    if (config.slots.chart.type === "funnel") {
      chartProps.chart = {
        type: "funnel",
        data: chartData,
        configuration: config.slots.chart.configuration
      };
    }
    if (isLineChartCard(config)) {
      logger.debug("LineChart", config.slots, cardData);
      chartProps.chart = mapLineChartProps(config, cardData, {
        translate,
        noComparison,
        resolveSeriesData
      }).chart;
      logger.debug("LineChart props is", chartProps);
    }
    return /* @__PURE__ */jsx(ChartTemplate, {
      ...chartProps,
      isLoading,
      maxHeight,
      noComparison,
      helpContent,
      style
    });
  }
  if (isTableTemplate(config)) {
    if (cardData === null || Object.keys(cardData).length === 0) {
      return null;
    }
    const t = translate;
    const {
      variant
    } = config;
    const tableData = config.slots.tabs ? config.slots.tabs.map(tab => getTableData(cardData, tab.slots.data)) : [getTableData(cardData, config.slots.table.data)];
    let t6;
    if ($[13] !== config.data?.sources) {
      t6 = config.data?.sources ?? {};
      $[13] = config.data?.sources;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const configSources = t6;
    let t7;
    if ($[15] !== configSources) {
      t7 = dataKey => configSources[dataKey]?.sortBy;
      $[15] = configSources;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const getSortBy = t7;
    const tableSortBys = config.slots.tabs ? config.slots.tabs.map(tab_0 => getSortBy(tab_0.slots.data.key)) : [getSortBy(config.slots.table?.data.key ?? "")];
    const tableColumns = translateTableConfigTitles(config.slots, t);
    const tabTitles = config.slots.tabs?.map(tab_1 => translate(tab_1.title?.text || "")) || [];
    const filteredColumns = noComparison ? tableColumns.map(_temp2) : tableColumns;
    const tables = tableData.map((data, index) => ({
      columns: filteredColumns[index] || [],
      defaultSortBy: tableSortBys[index],
      ...data
    }));
    const tableProps = {
      title: translateTitleSlot(config.slots.title),
      tabTitles,
      tables,
      showTabCounts: config.slots.tabs?.map(_temp3) || [],
      tabsDisplay: config.slots.tabs_display
    };
    return /* @__PURE__ */jsx(TableTemplate, {
      ...tableProps,
      className: variant,
      isLoading,
      maxHeight,
      helpContent
    });
  }
  if (isTextBlockTemplate(config)) {
    const {
      title,
      text
    } = config.slots;
    const t6 = title ? translateTitleSlot(title) : void 0;
    let t7;
    if ($[17] !== text || $[18] !== translate) {
      t7 = translate(text);
      $[17] = text;
      $[18] = translate;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== config.collapsible || $[21] !== helpContent || $[22] !== isLoading || $[23] !== t6 || $[24] !== t7) {
      t8 = /* @__PURE__ */jsx(TextBlockTemplate, {
        title: t6,
        textBlock: t7,
        helpContent,
        isLoading,
        collapsible: config.collapsible
      });
      $[20] = config.collapsible;
      $[21] = helpContent;
      $[22] = isLoading;
      $[23] = t6;
      $[24] = t7;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    return t8;
  }
  if (isBannerTemplate(config)) {
    const banner = config.slots;
    let t6;
    if ($[26] !== banner.header || $[27] !== translate) {
      t6 = translate(banner.header);
      $[26] = banner.header;
      $[27] = translate;
      $[28] = t6;
    } else {
      t6 = $[28];
    }
    let t7;
    if ($[29] !== banner.subtitle || $[30] !== translate) {
      t7 = banner.subtitle ? translate(banner.subtitle) : void 0;
      $[29] = banner.subtitle;
      $[30] = translate;
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    let t8;
    if ($[32] !== banner.image || $[33] !== components) {
      t8 = banner.image ? {
        ...banner.image,
        component: components.Image
      } : void 0;
      $[32] = banner.image;
      $[33] = components;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    let t9;
    if ($[35] !== banner.cta || $[36] !== components || $[37] !== onNavigate || $[38] !== translate) {
      t9 = banner.cta ? {
        label: translate(banner.cta.label),
        url: banner.cta.url,
        onNavigate,
        component: components.Button
      } : void 0;
      $[35] = banner.cta;
      $[36] = components;
      $[37] = onNavigate;
      $[38] = translate;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    let t10;
    if ($[40] !== t6 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
      const bannerProps = {
        header: t6,
        subtitle: t7,
        image: t8,
        cta: t9
      };
      t10 = /* @__PURE__ */jsx(BannerTemplate, {
        banner: bannerProps
      });
      $[40] = t6;
      $[41] = t7;
      $[42] = t8;
      $[43] = t9;
      $[44] = t10;
    } else {
      t10 = $[44];
    }
    return t10;
  }
  return null;
};
function _temp(col) {
  return !col.comparison;
}
function _temp2(tbl) {
  return tbl.filter(_temp);
}
function _temp3(tab_2) {
  return !!tab_2.include_count;
}
export { CardTemplate as default };